import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import Icon from '../../misc/Icon';
import Button from '../../misc/Button';
import DecoratorSettingsModal from './DecoratorSettingsModal';
import DecoratorIconOptions from '../../../constants/DecoratorIconOptions';
// ===================================================================
// Styled Components
// ===================================================================

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  margin: 0px 12px 12px 12px;
`;

const StyledAddWrapper = styled.div`
  flex: 1;
  transition: all 0.15s ease-in-out;
`;

const StyledDecoratorsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: 1px solid ${(props) => props.theme.borderColor};
  padding: 6px;
`;

const StyledDecorator = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: 1px solid ${(props) => props.theme.borderColor};
  color: ${(props) => props.theme.fontColorPrimary};
  padding: 6px;
  margin-bottom: 4px;
`;

const StyledDecoratorLabel = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  margin-right: 8px;
  font-size: 12px;
  align-items: center;
  gap: 4px;
  overflow: hidden;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
`;

const StyledColorDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${(props) => props.$color || '#266FFA'};
`;

const StyledDecoratorAction = styled.span`
  font-size: 10px;
  color: ${(props) => props.theme.fontColorSecondary};
  margin-left: 4px;
  flex-shrink: 0;
  white-space: nowrap;
`;

const StyledMenuIcon = styled.div`
  flex-shrink: 0;
  svg {
    font-size: 14px;
  }
`;

const StyledTrashIcon = styled.div`
  flex-shrink: 0;
  margin-left: 6px;
  svg {
    font-size: 14px;
  }
`;

// ===================================================================
// Helpers
// ===================================================================

const getIconDefinition = (iconValue) => {
  if (!iconValue) return null;
  const match = DecoratorIconOptions.find((opt) => opt.value === iconValue);
  return match ? match.icon : ThemeIcons.decorator.image;
};

// ===================================================================
// Component
// ===================================================================

const DecoratorSettings = ({
  decorators,
  onCreate,
  onChange,
  onDelete,
}) => {

  const theme = useContext(ThemeContext);
  const [displayModal, setDisplayModal] = useState(null);
  const [activeDecorator, setActiveDecorator] = useState(null);

  const validDecorators = decorators || [];

  const decoratorNodes = validDecorators.map((decorator) => {

    const iconDef = getIconDefinition(decorator.icon);

    return (
      <StyledDecorator key={decorator._id} theme={theme}>
        <StyledDecoratorLabel>
          {iconDef ? <Icon icon={iconDef} color={decorator.color} /> : null}
          <span>{decorator.label || ''}</span>
        </StyledDecoratorLabel>

        <StyledMenuIcon>
          <Icon
            icon={ThemeIcons.settings.edit}
            onClick={() => {
              setActiveDecorator(decorator);
              setDisplayModal(true);
            }}
          />
        </StyledMenuIcon>

        <StyledTrashIcon>
          <Icon
            icon={ThemeIcons.settings.delete}
            color={theme.dangerColor}
            onClick={() => {
              if (onDelete) onDelete(decorator._id);
            }}
          />
        </StyledTrashIcon>
      </StyledDecorator>
    );

  });

  return (
    <StyledWrapper>
      <StyledDecoratorsWrapper theme={theme}>
        {decoratorNodes}

        <StyledAddWrapper>
          <DecoratorSettingsModal
            display={displayModal}
            decorator={activeDecorator}
            onHide={() => {
              setActiveDecorator(null);
              setDisplayModal(false);
            }}
            onSubmit={(decoratorId, decorator) => {
              if (activeDecorator && activeDecorator._id) onChange(decoratorId, decorator);
              else onCreate(decorator);
            }}
          >
            <div>
              <Button
                styles={{ width: '100%', padding: '6px' }}
                icon={ThemeIcons.settings.optionAdd}
                onClick={() => setDisplayModal(true)}
              />
            </div>
          </DecoratorSettingsModal>
        </StyledAddWrapper>
      </StyledDecoratorsWrapper>
    </StyledWrapper>
  );

};

DecoratorSettings.propTypes = {
  decorators: PropTypes.array,
  onCreate: PropTypes.func,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
};

export default DecoratorSettings;
